import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { ProductSelectionData } from '../image-generation/ProductSelectionModal';
import taapService from '../../services/api/taapService';
import knowledgeApiService from '../../services/api/knowledge/knowledgeApiService';

/** Reload the authorized proposal instead of trusting a product or brief in the URL. */
const TaapBannerHandoff: React.FC<{ onApply: (data: ProductSelectionData) => void }> = ({ onApply }) => {
  const [params] = useSearchParams();
  const proposalId = params.get('taap_proposal');
  const productId = params.get('product');
  const ideaId = params.get('idea');
  const accountParam = params.get('account');
  const queryAccountId = accountParam && /^\d+$/.test(accountParam) ? Number(accountParam) : null;
  const applyRef = useRef(onApply);
  applyRef.current = onApply;
  const [state, setState] = useState<{ loading: boolean; error?: string; title?: string; brief?: string; accountId?: number | null }>({ loading: true });
  const backAccountId = state.accountId !== undefined ? state.accountId : queryAccountId;
  const backSearch = backAccountId && Number.isSafeInteger(backAccountId) && backAccountId > 0 ? `?account=${backAccountId}` : '';

  useEffect(() => {
    if (!proposalId) return;
    let cancelled = false;
    setState({ loading: true });
    const load = async () => {
      try {
        if (!productId || !ideaId || !/^\d+$/.test(productId)) throw new Error('invalid');
        const proposal = await taapService.getProposal(proposalId);
        const strategy = proposal.product_strategies?.find((item) => item.product_id === Number(productId));
        const idea = strategy?.ideas.find((item) => item.id === ideaId);
        if (!strategy || !idea) throw new Error('stale');
        const product = await knowledgeApiService.getProduct(strategy.product_id);
        if (cancelled) return;
        applyRef.current({
          product,
          selectedImages: [],
          segmentId: '',
          segmentName: idea.segment_name,
          appealId: idea.id,
          appealName: idea.appeal_name,
          appealDirection: idea.appeal_direction,
          additionalContext: idea.brief,
          includePerson: '',
        });
        setState({ loading: false, title: `${strategy.product_name} · ${idea.appeal_name}`, brief: idea.brief, accountId: proposal.ad_account_id ?? null });
      } catch {
        if (!cancelled) setState({ loading: false, error: '提案の引き継ぎに失敗しました。商品との紐づけや戦略が変わった可能性があります。提案詳細から候補を選び直してください。' });
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [proposalId, productId, ideaId]);

  if (!proposalId) return null;
  return (
    <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm" role={state.error ? 'alert' : 'status'}>
      {state.loading ? '提案の商品・訴求を読み込んでいます…' : state.error || (
        <>
          <p className="font-semibold text-gray-900">広告改善提案を引き継ぎました: {state.title}</p>
          <p className="mt-1 text-gray-600">商品・訴求と広告実績を入力済みです。</p>
          <details className="mt-2 text-gray-700">
            <summary className="cursor-pointer">引き継いだ内容を確認</summary>
            <p className="mt-2 whitespace-pre-wrap text-xs leading-relaxed">{state.brief}</p>
          </details>
        </>
      )}
      <Link to={`/taap/proposals/${encodeURIComponent(proposalId)}${backSearch}`} className="mt-1 inline-block text-blue-700 underline">提案詳細に戻る</Link>
    </div>
  );
};

export default TaapBannerHandoff;
